<template>
    <UiTooltip
        v-if="hasMultipleRates"
        placement="top"
        trigger-class="inline-flex items-center align-middle"
    >
        <template #trigger>
            <Icon
                icon="info"
                class="w-4 h-4 text-slate-400 dark:text-slate-500 cursor-help"
                aria-hidden="true"
            />
            <span class="sr-only">Transfer rate varies by card</span>
        </template>

        <div class="text-left">
            <p class="mb-1 font-medium">Transfer rate varies by card</p>
            <span v-for="(rate, idx) in distinctRates" :key="idx" class="block">
                {{ rateLabel(rate) }}:
                <template v-if="isBonused(rate)">
                    <span class="line-through opacity-60">
                        {{ rate.base_transfer_ratio }}
                    </span>
                    {{ rate.effective_transfer_ratio }}
                </template>
                <template v-else>{{ ratioOf(rate) }}</template>
            </span>
        </div>
    </UiTooltip>
</template>

<script setup>
import { computed } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import UiTooltip from '@/components/ui/tooltip/ui-tooltip.vue';

const props = defineProps({
    rates: {
        type: Array,
        default: () => [],
    },
});

const ratioOf = (rate) => rate.effective_transfer_ratio || rate.transfer_ratio;

// How many card names a line spells out before collapsing to "+N more".
// Per-card rate storage (MY1-3011) means a rate can legitimately carry a
// dozen cards; a tooltip listing them all is unreadable.
const MAX_CARD_NAMES = 3;

// Line label for a rate (MY1-2921 item 5). An explicit label is a
// human-authored grouping name (manual rates only, MY1-3011) and wins;
// otherwise the linked card names describe the rate, truncated past
// MAX_CARD_NAMES. "Standard rate" is reserved for the default entry — a
// non-default rate whose cards no longer resolve (dead card_id, a tolerated
// state) must not masquerade as a second, contradictory standard rate.
const rateLabel = (rate) => {
    if (rate.label) {
        return rate.label;
    }

    const cardTitles = (rate.cards || [])
        .map((card) => card?.title)
        .filter(Boolean);

    if (!cardTitles.length) {
        return rate.is_default ? 'Standard rate' : 'Other cards';
    }

    if (cardTitles.length <= MAX_CARD_NAMES) {
        return cardTitles.join(', ');
    }

    return `${cardTitles.slice(0, MAX_CARD_NAMES).join(', ')} +${cardTitles.length - MAX_CARD_NAMES} more`;
};

// A rate is bonus-adjusted when its effective ratio differs from its base — the
// active transfer bonus is folded into every rate's effective_transfer_ratio.
const isBonused = (rate) =>
    rate.base_transfer_ratio &&
    rate.effective_transfer_ratio &&
    rate.base_transfer_ratio !== rate.effective_transfer_ratio;

// De-duplicate the lines by (label, base, effective) so identical rates don't repeat.
const distinctRates = computed(() => {
    const seen = new Set();
    const out = [];
    for (const rate of props.rates || []) {
        const ratio = ratioOf(rate);
        if (!ratio) continue;
        const key = `${rateLabel(rate)}|${rate.base_transfer_ratio || ''}|${ratio}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(rate);
    }
    return out;
});

// Only surface the breakdown when the rate genuinely varies — i.e. there are at
// least two distinct ratios across the cards. Multiple identical rates (or a
// single labelled rate matching the default) don't count.
const hasMultipleRates = computed(() => {
    const ratios = new Set(
        (props.rates || []).map((rate) => ratioOf(rate)).filter(Boolean)
    );
    return ratios.size > 1;
});
</script>
